import {
  hotkeyPrimaryOrOptionalCodeMatches,
  keyboardModifiersMatchHotkeyBase,
} from './hotkeyFunctions.ts';
import type { Hotkey, ModifierType } from './hotkeys';

/**
 * Hold-to-repeat: while a key chord is held, `onTick` runs on an interval (and optionally once
 * immediately on press). `onRelease` runs when the primary key or a required modifier is released,
 * or when `dispose` / window blur runs.
 */
export type HoldRepeatWhileKeyHeldBinding = {
  hotkey: Hotkey;
  onTick: () => void;
  onRelease: () => void;
};

const repeatChordId = (h: Hotkey) =>
  `${h.primary.code}\0${h.optionalPrimary?.code ?? ''}\0${h.hotkeyModifier?.modifier ?? ''}\0${h.secondaryHotkeyModifier?.modifier ?? ''}`;

const modifierKeyReleaseMatches = (evCode: string, mod: ModifierType | undefined): boolean => {
  if (!mod) return false;
  if (mod === 'Shift') return evCode === 'ShiftLeft' || evCode === 'ShiftRight';
  if (mod === 'Ctrl') return evCode === 'ControlLeft' || evCode === 'ControlRight';
  if (mod === 'Alt' || mod === 'Option') return evCode === 'AltLeft' || evCode === 'AltRight';
  return false;
};

const shouldEndHold = (ev: KeyboardEvent, b: HoldRepeatWhileKeyHeldBinding): boolean => {
  if (hotkeyPrimaryOrOptionalCodeMatches(b.hotkey, ev.code)) return true;
  return (
    modifierKeyReleaseMatches(ev.code, b.hotkey.hotkeyModifier?.modifier) ||
    modifierKeyReleaseMatches(ev.code, b.hotkey.secondaryHotkeyModifier?.modifier)
  );
};

type ActiveRepeat = {
  timerId: number;
  onRelease: () => void;
  binding: HoldRepeatWhileKeyHeldBinding;
};

type CreateHoldRepeatWhileKeyHeldParams = {
  bindings: HoldRepeatWhileKeyHeldBinding[];
  intervalMs: number;
  /** If true, fire `onTick` once immediately on keydown (interval ticks follow after `intervalMs`). */
  callOnFirstTick: boolean;
};

/**
 * Stateful handlers for `keydown` / `keyup` (use the same instance for both). Ignores OS
 * `keydown.repeat` while a chord is already tracked so repeat comes only from `setInterval`.
 */
export const createHoldRepeatWhileKeyHeldHandlers = ({
  bindings,
  intervalMs,
  callOnFirstTick,
}: CreateHoldRepeatWhileKeyHeldParams) => {
  const active = new Map<string, ActiveRepeat>();

  const findBindingForKeyDown = (ev: KeyboardEvent) =>
    bindings.find(
      b =>
        hotkeyPrimaryOrOptionalCodeMatches(b.hotkey, ev.code) &&
        keyboardModifiersMatchHotkeyBase(ev, b.hotkey)
    );

  const onKeyDown = (ev: KeyboardEvent): boolean => {
    const b = findBindingForKeyDown(ev);
    if (!b) return false;

    if (ev.repeat) {
      ev.preventDefault();
      return true;
    }

    const id = repeatChordId(b.hotkey);
    if (active.has(id)) {
      ev.preventDefault();
      return true;
    }

    ev.preventDefault();

    if (callOnFirstTick) {
      b.onTick();
    }

    const timerId = window.setInterval(() => {
      b.onTick();
    }, intervalMs);

    active.set(id, { timerId, onRelease: b.onRelease, binding: b });
    return true;
  };

  const onKeyUp = (ev: KeyboardEvent): boolean => {
    let handled = false;
    for (const [id, entry] of active) {
      if (!shouldEndHold(ev, entry.binding)) continue;
      ev.preventDefault();
      window.clearInterval(entry.timerId);
      active.delete(id);
      entry.onRelease();
      handled = true;
    }
    return handled;
  };

  const dispose = () => {
    for (const [, entry] of active) {
      window.clearInterval(entry.timerId);
      entry.onRelease();
    }
    active.clear();
  };

  return { onKeyDown, onKeyUp, dispose };
};
